import type { ResolvedConfig, ViteDevServer } from "vite";
import * as esbuild from "esbuild";
import fs from "fs/promises";
import path from "path";
import resolveConfig from "tailwindcss/resolveConfig.js";

export interface TailwindConfigFeature {
  onConfigResolved(config: ResolvedConfig): void;
  onBuildStart(): Promise<void>;
  onConfigureServer(server: ViteDevServer): void;
}

const V4_CSS_CANDIDATES = [
  "src/styles.css",
  "src/index.css",
  "src/globals.css",
  "src/app.css",
];

const V4_NAMESPACES: Record<string, string> = {
  colors: "--color",
  screens: "--breakpoint",
  spacing: "--spacing",
  borderRadius: "--radius",
  fontFamily: "--font",
  opacity: "--opacity",
};

const OUTFILE_RELATIVE = "./src/tailwind.config.vibe.json";
const INTERMEDIATE_PREFIX = "./.vibe.tailwind.config";
const V3_CONFIG_RELATIVE = "./tailwind.config.ts";

// Distinct name per compile so overlapping v3 runs never `esbuild` to and
// `unlink` each other's intermediate import target.
let intermediateSeq = 0;
function intermediateRelative(): string {
  intermediateSeq += 1;
  return `${INTERMEDIATE_PREFIX}.${process.pid}.${intermediateSeq}.js`;
}

async function findV4CssEntry(projectRoot: string): Promise<string | null> {
  for (const candidate of V4_CSS_CANDIDATES) {
    const abs = path.resolve(projectRoot, candidate);
    try {
      const contents = await fs.readFile(abs, "utf8");
      if (/@import\s+["']tailwindcss/.test(contents)) return abs;
    } catch {
      // missing file — try next
    }
  }
  return null;
}

type LoadDesignSystem = (
  css: string,
  opts: { base: string },
) => Promise<{
  theme: {
    namespace(ns: string): Iterable<[string | null, string]>;
  };
}>;

async function loadDesignSystemFromProject(): Promise<LoadDesignSystem | null> {
  try {
    // Dynamic import so consumers not on v4 don't need the package installed.
    const modName = "@tailwindcss/node";
    const mod = (await import(modName)) as {
      __unstable__loadDesignSystem?: LoadDesignSystem;
    };
    return mod.__unstable__loadDesignSystem ?? null;
  } catch {
    return null;
  }
}

function parseRootVars(css: string): Record<string, string> {
  const vars: Record<string, string> = {};
  const rootMatch = css.match(/:root\s*\{([^}]*)\}/s);
  if (!rootMatch) return vars;
  for (const decl of rootMatch[1]!.split(";")) {
    const trimmed = decl.trim();
    if (!trimmed.startsWith("--")) continue;
    const colon = trimmed.indexOf(":");
    if (colon === -1) continue;
    const key = trimmed.slice(0, colon).trim();
    const value = trimmed.slice(colon + 1).trim();
    if (key && value) vars[key] = value;
  }
  return vars;
}

function resolveVars(
  value: string,
  rootVars: Record<string, string>,
  depth = 0,
): string {
  if (depth > 8) return value;
  return value.replace(
    /var\(\s*(--[\w-]+)(?:\s*,\s*([^)]+))?\s*\)/g,
    (match, name: string, fallback: string | undefined) => {
      const resolved = rootVars[name];
      if (resolved !== undefined) return resolveVars(resolved, rootVars, depth + 1);
      return fallback ?? match;
    },
  );
}

async function generateV4Config(
  cssEntry: string,
  outfile: string,
  load: LoadDesignSystem,
): Promise<void> {
  const css = await fs.readFile(cssEntry, "utf8");
  const ds = await load(css, { base: path.dirname(cssEntry) });
  const rootVars = parseRootVars(css);
  const theme: Record<string, Record<string, string>> = {};
  for (const [configKey, namespace] of Object.entries(V4_NAMESPACES)) {
    const entries: Record<string, string> = {};
    for (const [key, rawValue] of ds.theme.namespace(namespace)) {
      if (key === null) continue;
      entries[key] = resolveVars(rawValue, rootVars);
    }
    theme[configKey] = entries;
  }
  await fs.mkdir(path.dirname(outfile), { recursive: true });
  await fs.writeFile(outfile, JSON.stringify({ theme }, null, 2));
}

async function generateV3Config(
  tailwindInputFile: string,
  tailwindIntermediateFile: string,
  tailwindJsonOutfile: string,
): Promise<void> {
  await esbuild.build({
    entryPoints: [tailwindInputFile],
    outfile: tailwindIntermediateFile,
    bundle: true,
    format: "esm",
    banner: {
      js: 'import { createRequire } from "module"; const require = createRequire(import.meta.url);',
    },
  });
  try {
    const userConfig = (await import(
      tailwindIntermediateFile + "?update=" + Date.now()
    )) as { default?: unknown };
    if (!userConfig || !userConfig.default) {
      throw new Error("Invalid Tailwind config structure");
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resolved = (resolveConfig as any)(userConfig.default);
    await fs.writeFile(tailwindJsonOutfile, JSON.stringify(resolved, null, 2));
  } finally {
    await fs.unlink(tailwindIntermediateFile).catch(() => {});
  }
}

export function createTailwindConfigFeature(): TailwindConfigFeature {
  let projectRoot = "";
  let running = false;
  let rerunQueued = false;

  // Serialize compiles: a change arriving mid-run queues exactly one re-run
  // rather than racing a concurrent write to the same output file.
  const run = async (): Promise<void> => {
    if (running) {
      rerunQueued = true;
      return;
    }
    running = true;
    try {
      await runOnce();
    } finally {
      running = false;
      if (rerunQueued) {
        rerunQueued = false;
        void run();
      }
    }
  };

  const runOnce = async (): Promise<void> => {
    if (!projectRoot) return;
    const outfile = path.resolve(projectRoot, OUTFILE_RELATIVE);

    // Prefer v4 when the CSS entry uses `@import "tailwindcss"` and
    // @tailwindcss/node is available.
    const cssEntry = await findV4CssEntry(projectRoot);
    if (cssEntry) {
      const load = await loadDesignSystemFromProject();
      if (load) {
        try {
          await generateV4Config(cssEntry, outfile, load);
          console.log(`[vibe-tagger] wrote ${outfile}`);
          return;
        } catch (error) {
          console.error("[vibe-tagger] Error generating v4 tailwind config:", error);
          return;
        }
      }
    }

    // Fall back to v3: compile tailwind.config.ts via esbuild, then resolveConfig.
    const v3ConfigFile = path.resolve(projectRoot, V3_CONFIG_RELATIVE);
    try {
      await fs.access(v3ConfigFile);
    } catch {
      return;
    }
    const intermediate = path.resolve(projectRoot, intermediateRelative());
    try {
      await generateV3Config(v3ConfigFile, intermediate, outfile);
      console.log(`[vibe-tagger] wrote ${outfile}`);
    } catch (error) {
      console.error("[vibe-tagger] Error generating v3 tailwind config:", error);
    }
  };

  return {
    onConfigResolved(config) {
      projectRoot = config.root;
    },
    async onBuildStart() {
      await run();
    },
    onConfigureServer(server) {
      try {
        const watchPaths = [
          path.resolve(projectRoot, V3_CONFIG_RELATIVE),
          ...V4_CSS_CANDIDATES.map((candidate) =>
            path.resolve(projectRoot, candidate),
          ),
        ];
        for (const watchPath of watchPaths) server.watcher.add(watchPath);

        const normalized = new Set(
          watchPaths.map((watchPath) => path.normalize(watchPath)),
        );
        server.watcher.on("change", async (changedPath) => {
          if (normalized.has(path.normalize(changedPath))) await run();
        });
      } catch (error) {
        console.error(
          "[vibe-tagger] Error adding Tailwind inputs to Vite watcher:",
          error,
        );
      }
    },
  };
}
